import { ManagementCard } from '../manage/ManagementCard';
import {
  Box,
  Checkbox,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import { createArticleStyles as styles } from '@texas/resources/theme/styles/entityWorkspace';
import { WarningComponent } from '@texas/components/shared/WarningComponent';
import { useActiveSession } from '@texas/hooks/useSession';
import { CertificateConfigurationSelect } from '../CertificateConfigurationSelect';
import { useTranslation } from 'react-i18next';
import { useCreateArticle } from './useCreateArticle';

export function CreateArticleBranches() {
  const { currentUser } = useActiveSession();
  const {
    resources,
    branchIds: selected,
    branchCertificates: certificates,
    setValue,
    getValues,
    fieldWarnings,
  } = useCreateArticle();
  const branches = resources.branches.data;
  const regions = resources.regions.data;
  const onChange = (ids: number[]) => setValue('branchIds', ids);
  const onCertificatesChange = (branchId: number, ids: number[]) =>
    setValue('branchCertificates', {
      ...getValues('branchCertificates'),
      [branchId]: ids,
    });
  const { t } = useTranslation();
  return (
    <ManagementCard
      className="create-article-card"
      title={t(
        currentUser.betaFeatures
          ? 'article.create.certificates'
          : 'article.management.branches',
      )}
      description={t(
        currentUser.betaFeatures
          ? 'article.create.certificatesHint'
          : 'article.create.branchesHint',
      )}
    >
      <Box sx={styles.stack} id={fieldWarnings.branches.targetId}>
        <Box sx={styles.row}>
          <WarningComponent {...fieldWarnings.branches}>
            <Text sx={styles.regionLabel}>{t('article.copy.branches')}</Text>
          </WarningComponent>
          {regions.map((region) => (
            <BranchSelector
              key={region.id}
              showLabel={true}
              ids={region.branches
                .filter((b) => branches.some((branch) => branch.id === b.id))
                .map((b) => b.id)}
              label={region.name}
              selected={selected}
              onChange={onChange}
            />
          ))}
          <Text sx={styles.branchCount}>
            {t('article.create.branchesSelected', {
              selected: selected.length,
              total: branches.length,
            })}
          </Text>
        </Box>
        <Box sx={styles.tableScroll}>
          <Table sx={styles.branchTable}>
            <Thead>
              <Tr>
                <Th>
                  <BranchSelector
                    ids={branches.map((b) => b.id)}
                    label={t('general.selectAll')}
                    selected={selected}
                    onChange={onChange}
                  />
                </Th>
                <Th>{t('general.name')}</Th>
                <Th>{t('configuration.identifier')}</Th>
                <Th>{t('article.create.region')}</Th>
                {currentUser.betaFeatures && (
                  <Th>{t('article.create.certificateLabel')}</Th>
                )}
              </Tr>
            </Thead>
            <Tbody>
              {branches.map((branch) => (
                <Tr key={branch.id}>
                  <Td>
                    <BranchSelector
                      ids={[branch.id]}
                      label={branch.name}
                      selected={selected}
                      onChange={onChange}
                    />
                  </Td>
                  <Td>{branch.name}</Td>
                  <Td>{branch.identifier}</Td>
                  <Td>
                    {regions
                      .filter((r) => r.branches.some((b) => b.id === branch.id))
                      .map((r) => r.name)
                      .join(', ') || t('general.none')}
                  </Td>
                  {currentUser.betaFeatures && (
                    <Td>
                      {selected.includes(branch.id) && (
                        <CertificateConfigurationSelect
                          branchName={branch.name}
                          definitions={resources.certificates.data ?? []}
                          value={certificates[branch.id] ?? []}
                          onChange={(ids) =>
                            onCertificatesChange(branch.id, ids)
                          }
                        />
                      )}
                    </Td>
                  )}
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      </Box>
    </ManagementCard>
  );
}

interface BranchSelectorProps {
  ids: number[];
  label: string;
  showLabel?: boolean;
  selected: number[];
  onChange: (ids: number[]) => void;
}

function BranchSelector({
  ids,
  label,
  showLabel,
  selected,
  onChange,
}: BranchSelectorProps) {
  const count = ids.filter((id) => selected.includes(id)).length;
  return (
    <Checkbox
      aria-label={label}
      isDisabled={!ids.length}
      isChecked={!!ids.length && count === ids.length}
      isIndeterminate={count > 0 && count < ids.length}
      onChange={(e) =>
        onChange(
          e.target.checked
            ? Array.from(new Set([...selected, ...ids]))
            : selected.filter((id) => !ids.includes(id)),
        )
      }
    >
      {showLabel && label}
    </Checkbox>
  );
}
